/**
 *
 * ExcusedStudents
 *
 */

import React, { memo, useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { List, Empty } from "antd";

// icons
import CustomIcons from "components/CustomIcons";

// utils
import {
  generateAbsenceReason,
  // new
  getExcusedStudents,
} from "utils/helpers/helper.generateData";
import { getUniqueArrayObj, getObjId } from "utils/helpers/helper.general";
import { convertTimeToString, convertDateToWeekDay, convertDateToString } from "utils/moment/moment.utils";

function ExcusedStudents({
  dataSource,
  loading,
  course,
  onDate,
  absences,
  attendances,
  // default props
  className,
  children,
}) {
  const childClassNames = classNames("excused-students", className);
  const { t, i18n } = useTranslation();

  const [excusedStudents, setExcusedStudents] = useState([]);

  useEffect(() => {
    if (dataSource) {
      const originalAbs = absences?.filter(a => a?.status != "refused") || [];
      const excused = getExcusedStudents(dataSource, originalAbs, attendances);
      let excusedUnique = getUniqueArrayObj(excused, "_id");
      excusedUnique = excusedUnique?.sort((a, b) =>
        a?.description?.lastname.localeCompare(b?.description?.lastname),
      );
      excused && setExcusedStudents(excusedUnique);
    }
  }, [dataSource, onDate, course, absences]);

  const stdReason = useCallback(absData => generateAbsenceReason(absData), []);

  const itemAbsTimeRange = useCallback(element => { 
    return (
      <div className="absence-wrapper-date">

        <div className="absence-date__row">
          <div className="column helper">{t("common.from")}</div>
          <div className="column day">{!!element?.date_from && convertDateToWeekDay(element?.date_from, "dd")}{' '}</div>
          <div className="column">
            {!!element?.date_from && convertDateToString(element?.date_from)}
          </div>
          <div className="column time">
            {!!element?.time_from && convertTimeToString(element?.time_from)}
            {(!!!element?.date_to && !!!element?.recurring_date && !!element?.time_to) && (
              ' - '+ convertTimeToString(element?.time_to)
            )}
          </div>
        </div>
        {(!!element?.date_to || !!element?.recurring_date) && (
          <div className="absence-date__row">
            <div className="column helper">{t("common.to")}</div>
            <div className="column day">
              {!!element?.recurring_date
                ? convertDateToWeekDay(element?.recurring_date, "dd")
                : (
                  !!element?.date_to
                  ? convertDateToWeekDay(element?.date_to, "dd")
                  : convertDateToWeekDay(element?.date_from, "dd")
                )}{' '}
            </div>
            <div className="column">
              {!!element?.recurring_date
                ? convertDateToString(element?.recurring_date)
                : (
                  !!element?.date_to
                  ? convertDateToString(element?.date_to)
                  : convertDateToString(element?.date_from)
                )}
            </div>
            <div className="column time">
              {!!element?.time_to ? convertTimeToString(element?.time_to) : convertTimeToString(element?.time_from)}
            </div>
          </div>
        )}
        
        {/* {absenceDuration && (
          <div className="absence-date__row">
            <div className="column helper big">t("absences.duration"):</div>
            <div className="column">
              <span className="absence-time">{absenceDuration}</span>
            </div>
          </div>
        )} */}
      </div>
    );
  }, []);

  const renderItem = useCallback(
    item => {
      let abs = null;
      const currentAbs = absences?.filter(abs => getObjId(item) == getObjId(abs?.student));

      const hasSickness = currentAbs?.filter(absence => absence?.status == "sick") || [];

      abs = !!item?.hasAbs && item?.hasAbs?.[0] || currentAbs?.length > 0 && currentAbs?.[0];
      if (hasSickness?.length > 0) {
        abs = hasSickness?.[0];
      }
  
      const reason = stdReason(abs);
      return (
        item && (
          <List.Item key={item?._id + "-excused"}>
            <CustomIcons type="person" />
            <List.Item.Meta
              title={`${item?.description?.lastname} ${
                item?.description?.firstname
              }`}
              description={
                <>
                  {reason}
                  {!!abs && itemAbsTimeRange(abs)}
                </>
              }
            />
          </List.Item>
        )
      );
    },
    [course, course?.students, onDate, absences, attendances],
  );

  const renderList = useMemo(
    () => (
      <List
        className="excused-students__list"
        dataSource={excusedStudents || []}
        loading={loading}
        renderItem={renderItem}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t("errors.noData")}
            />
          ),
        }}
      />
    ),
    [excusedStudents, loading, renderItem, i18n?.language],
  );

  return (
    <div className={childClassNames}>
      <h3 className="excused-students__title">{t("absences.listOfAbsences")}</h3>
      {renderList}
      {children && <div className="excused-students__children">{children}</div>}
    </div>
  );
}

ExcusedStudents.defaultProps = {
  mode: "simple",
  absences: [],
  attendances: [],
};
ExcusedStudents.propTypes = {
  mode: PropTypes.oneOf(["simple", "with-action"]).isRequired,
  course: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
  ]),
  onDate: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.string.isRequired,
    PropTypes.object.isRequired,
  ]),
  dataSource: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.array.isRequired,
  ]),
};

export default memo(ExcusedStudents);
